import { useState } from "react";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import SnackbarMessage from "../../components/snackBar/snackBar";
import { FieldTextField } from "../../components/selections/fieldInputs";
import { nameColor, nameInitials } from "../branchDepartment/components/nameCardList";
import { branchThunk, addBranchThunk, departmentThunk, addDepartmentThunk } from "../../components/general/generalThunk";
import { getOrganizationsThunk, getTagsThunk, addOrganizationThunk, addTagThunk } from '../contact/contactThunk';
import { QR, qrCardSx, QrActionButton } from "../qrCode/components/qrUi";
import CreateBranchDepartment from "./createBranchDepartment";

// Small rounded icon tile used in the section heading / empty state
const iconTileSx = (color, size = 36) => ({
    width: size,
    height: size,
    borderRadius: 2,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color,
    bgcolor: `${color}14`,
    "& svg": { fontSize: size * 0.55 },
});


export default function BranchDeptOrganizations() {

    const [search, setSearch] = useState("");

    const { status, branches, departments, branchDepartments } = useSelector((state) => state.general);
    const { getTags, getOrganizations } = useSelector((state) => state.contacts);

    const getValueLabels = (data, labelKey) => {
        return data.map((item) => ({
            value: item.id,
            label: item[labelKey],
        }));
    };

    const dispatch = useDispatch();

    const [formValue, setFormValue] = useState("");
    const [isCreate, setIsCreate] = useState(false);
    const [selectedOption, setSelectedOption] = useState(5);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const handleChange = (event) => {
        setSelectedOption(Number(event.target.value));
        setFormValue("");
        setIsCreate(false);
        setSnackbar(prev => ({
            ...prev,
            open: false,
            message: "",
            severity: "success",
        }));
    };

    const handleCreate = () => {
        setIsCreate((prev) => !prev);
        setFormValue("");
        setSnackbar(prev => ({
            ...prev,
            open: false,
            message: "",
            severity: "success",
        }));
    };

    const options = [
        { id: 1, label: "Organizations" },
        { id: 2, label: "Branch" },
        { id: 3, label: "Department" },
        { id: 4, label: "Branch Departments" },
        { id: 5, label: "Tags" },
    ];

    const getDisplayData = () => {
        switch (selectedOption) {
            // case 1:
            //     return userdata.organization_names?.split(",") || [];
            // case 2:
            //     return userdata.branch_name?.split(",") || [];
            // case 3:
            //     return userdata.department_name?.split(",") || [];
            // case 4:
            //     return userdata.branch_department_name?.split(",") || [];
            // case 5:
            //     return userdata.tag_names?.split(",") || [];
            // default:
            //     return [];

            case 1:
                return getValueLabels(getOrganizations, "organization_name");
            case 2:
                return getValueLabels(branches, "branch_name");
            case 3:
                return getValueLabels(departments, "department_name");
            case 4:
                return getValueLabels(branchDepartments, "branch_department_name");
            case 5:
                return getValueLabels(getTags, "tag_name");
            default:
                return [];
        }
    };

    const displayData = getDisplayData();

    const getLabelById = (id) => {
        switch (id) {
            case 1:
                return "Organizations";
            case 2:
                return "Branches";
            case 3:
                return "Departments";
            case 4:
                return "Branch Departments";
            case 5:
                return "Tags";
            default:
                return "";
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formValue.trim()) {
            setSnackbar({
                open: true,
                message: `${getLabelById(selectedOption)} Name is required`,
                severity: "error",
            });
            return;
        }


        try {
            let result;

            if (selectedOption === 1) {
                result = await dispatch(addOrganizationThunk({ credentials: { organization_name: formValue.trim() } }));
            } else if (selectedOption === 2) {
                result = await dispatch(addBranchThunk({ credentials: { branch_name: formValue.trim() } }));
            } else if (selectedOption === 3) {
                result = await dispatch(addDepartmentThunk({ credentials: { department_name: formValue.trim() } }));
            } else if (selectedOption === 5) {
                result = await dispatch(addTagThunk({ credentials: { tag_name: formValue.trim() } }));
            }

            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });

                setIsCreate(false);
                setFormValue("");
                if (selectedOption === 1) {
                    dispatch(getOrganizationsThunk({ type: "getOrganizations" }));
                } else if (selectedOption === 2) {
                    dispatch(branchThunk({ type: "getBranch" }));
                } else if (selectedOption === 3) {
                    dispatch(departmentThunk({ type: "getDepartment" }));
                } else if (selectedOption === 5) {
                    dispatch(getTagsThunk({ type: "tags" }));
                }

            } else {
                setSnackbar({
                    open: true,
                    message: result.payload,
                    severity: "error",
                });

                setIsCreate(false);
                setFormValue("");
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }
    };

    const handleClose = () => {
        setIsCreate(false);
    }

    const filteredData = displayData.filter((item) => item.label.toLowerCase().includes(search.toLowerCase()));
    const sectionLabel = getLabelById(selectedOption);

    return (
        <Box>
            {/* <FormControl component="fieldset">
                <RadioGroup name="organizationOptions" value={selectedOption} onChange={handleChange} row>
                    {options.map((opt) => (
                        <FormControlLabel key={opt.id} value={opt.id} control={<Radio />} label={opt.label} />
                    ))}
                </RadioGroup>
            </FormControl> */}

            <Box sx={{ ...qrCardSx, bgcolor: "#fff" }}>
                {/* Section heading */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1.5}
                    sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: `1px solid ${QR.border}` }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                        <Box sx={iconTileSx(QR.main)}>
                            <LocalOfferOutlinedIcon />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
                                {sectionLabel}
                            </Typography>
                            <Typography sx={{ fontSize: 12.5, color: QR.slate }}>
                                {displayData.length} {displayData.length === 1 ? "item" : "items"}
                            </Typography>
                        </Box>
                    </Stack>

                    <QrActionButton
                        variant={isCreate ? "soft" : "solid"}
                        color={isCreate ? QR.slate : QR.main}
                        gradientTo={QR.light}
                        icon={isCreate ? <CloseIcon sx={{ fontSize: 17 }} /> : <AddIcon sx={{ fontSize: 17 }} />}
                        label={isCreate ? "Close" : "Create"}
                        onClick={handleCreate}
                    />
                </Stack>

                {/* Create form / search */}
                <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2.5 }}>
                    {
                        isCreate ? (
                            <Box sx={{ p: { xs: 1.75, sm: 2 }, borderRadius: 3, bgcolor: "#f8fafc", border: `1px dashed #dbe3ea` }}>
                                {
                                    selectedOption === 4 ? (
                                        <CreateBranchDepartment handleClose={handleClose} />
                                    ) : (
                                        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "flex-end" }} spacing={1.5}>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <FieldTextField
                                                    name="createName"
                                                    label={`${sectionLabel} Name`}
                                                    placeholder={`Enter ${sectionLabel.toLowerCase()} name`}
                                                    value={formValue}
                                                    maxLength={20}
                                                    required
                                                    icon={<LocalOfferOutlinedIcon />}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value.startsWith(" ")) return;
                                                        setFormValue(value);
                                                    }}
                                                    error={!formValue.trim() && snackbar.severity === "error"}
                                                />
                                            </Box>
                                            <QrActionButton
                                                variant="solid"
                                                color={QR.main}
                                                gradientTo={QR.light}
                                                icon={<CheckIcon sx={{ fontSize: 17 }} />}
                                                label="Submit"
                                                loading={status === "loading"}
                                                loadingLabel="Creating.."
                                                onClick={handleSubmit}
                                            />
                                        </Stack>
                                    )
                                }
                            </Box>
                        ) : (
                            <Box sx={{ width: { xs: "100%", sm: 300 } }}>
                                <FieldTextField
                                    name="tagSearch"
                                    label={`Search ${sectionLabel}`}
                                    placeholder="Search tag"
                                    value={search}
                                    icon={<SearchIcon />}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (!value.startsWith(" ")) {
                                            setSearch(value)
                                        }
                                    }}
                                />
                            </Box>
                        )
                    }
                </Box>

                {/* List */}
                <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
                    {filteredData.length !== 0 ? (
                        // Same card look as the Tags list (NameCardList): initials avatar, colour edge, dot pattern
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(auto-fill, minmax(150px, 1fr))", sm: "repeat(auto-fill, minmax(170px, 1fr))" }, gap: "10px" }}>
                            {filteredData.map((item, index) => {
                                const name = item.label || "";
                                const c = nameColor(name);
                                return (
                                    <Box
                                        key={index}
                                        title={name}
                                        sx={{
                                            position: "relative",
                                            overflow: "hidden",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.25,
                                            minWidth: 0,
                                            minHeight: 56,
                                            py: 1,
                                            pl: 1.25,
                                            pr: 1,
                                            borderRadius: 2.5,
                                            background: [
                                                `radial-gradient(circle at 100% 0%, ${c}26 0, ${c}00 46px)`,
                                                `linear-gradient(135deg, #ffffff 0%, #ffffff 45%, ${c}12 100%)`,
                                            ].join(", "),
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                bottom: 0,
                                                right: 0,
                                                width: "30%",
                                                backgroundImage: `radial-gradient(${c}55 1.1px, transparent 1.5px)`,
                                                backgroundSize: "12px 12px",
                                                WebkitMaskImage: "linear-gradient(90deg, transparent, #000 75%)",
                                                maskImage: "linear-gradient(90deg, transparent, #000 75%)",
                                                pointerEvents: "none",
                                            },
                                            border: `1px solid ${c}2e`,
                                            borderLeft: `3px solid ${c}`,
                                            boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
                                            transition: "all .18s ease",
                                            "& .card-mark": { transition: "all .25s ease" },
                                            "&:hover": {
                                                transform: "translateY(-2px)",
                                                borderColor: `${c}66`,
                                                borderLeftColor: c,
                                                boxShadow: `0 10px 20px -12px ${c}`,
                                                "& .card-mark": { opacity: 0.16, transform: "rotate(-8deg) scale(1.08)" },
                                            },
                                        }}
                                    >
                                        <Box className="card-mark" sx={{ position: "absolute", right: -6, bottom: -8, display: "flex", color: c, opacity: 0.08, pointerEvents: "none", "& svg": { fontSize: 52 } }}>
                                            <LocalOfferOutlinedIcon />
                                        </Box>
                                        <Avatar sx={{ position: "relative", width: 34, height: 34, flexShrink: 0, fontSize: 12.5, fontWeight: 800, color: "#fff", background: `linear-gradient(135deg, ${c}, ${c}b3)`, boxShadow: `0 0 0 3px #fff, 0 0 0 4px ${c}40` }}>
                                            {nameInitials(name)}
                                        </Avatar>
                                        <Typography sx={{ position: "relative", flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, color: "#0f172a", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3 }}>
                                            {name}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    ) : (
                        <Stack alignItems="center" spacing={1} sx={{ py: 4, textAlign: "center" }}>
                            <Box sx={iconTileSx(QR.slate, 48)}>
                                {search ? <SearchIcon /> : <LocalOfferOutlinedIcon />}
                            </Box>
                            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "#334155" }}>
                                No {sectionLabel.toLowerCase()} found
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: QR.slate, maxWidth: 320 }}>
                                {search ? "Try a different search term." : `Click "Create" to add your first ${sectionLabel.toLowerCase()}.`}
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Box>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </Box>
    );
}
